import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Select, Row, Col, Typography, Card, Table } from 'antd';
import type { DealType, DealRewards } from '@/types';
import { adminApi } from '@services/api';
import { DEFAULT_MAX_PLAYER_INDEX } from '@/constants/playerData';

const { Text } = Typography;
const { Option } = Select;

// §2.7 — division-tier names + in-code default evo-token amounts.
// Source: BASE_DROP_AMOUNTS + EVO_TOKENS_BY_DIVISION in
// wcc4-backend/src/modules/daily-deals/services/daily-drop-reward.service.ts.
// Migration 1779260000000-AddDailyDropDefaults backfills these into the
// `free_drop` row's `rewards.baseAmounts` + `rewards.evoTokensByDivision`
// keys for existing installs; new installs get them via the seed service.
// Placeholders below show these defaults so admins know what the field
// will revert to if cleared.
const DIVISION_TIERS: Array<{
  tier: number;
  name: string;
  defaultEvo: number;
}> = [
  { tier: 1, name: 'Rookie III', defaultEvo: 5 },
  { tier: 2, name: 'Rookie II', defaultEvo: 6 },
  { tier: 3, name: 'Rookie I', defaultEvo: 7 },
  { tier: 4, name: 'Amateur III', defaultEvo: 8 },
  { tier: 5, name: 'Amateur II', defaultEvo: 10 },
  { tier: 6, name: 'Amateur I', defaultEvo: 12 },
  { tier: 7, name: 'Pro III', defaultEvo: 15 },
  { tier: 8, name: 'Pro II', defaultEvo: 18 },
  { tier: 9, name: 'Pro I', defaultEvo: 20 },
  { tier: 10, name: 'Star III', defaultEvo: 25 },
  { tier: 11, name: 'Star II', defaultEvo: 30 },
  { tier: 12, name: 'Star I', defaultEvo: 35 },
  { tier: 13, name: 'Elite III', defaultEvo: 40 },
  { tier: 14, name: 'Elite II', defaultEvo: 45 },
  { tier: 15, name: 'Elite I', defaultEvo: 50 },
  { tier: 16, name: 'Master III', defaultEvo: 60 },
  { tier: 17, name: 'Master II', defaultEvo: 70 },
  { tier: 18, name: 'Master I', defaultEvo: 80 },
  { tier: 19, name: 'Legendary III', defaultEvo: 90 },
  { tier: 20, name: 'Legendary II', defaultEvo: 100 },
  { tier: 21, name: 'Legendary I', defaultEvo: 120 },
];

const DEFAULT_BASE_AMOUNTS = {
  coins: 250,
  matchTokens: 2,
  evoTokens: 2,
  cards: 1,
};

interface RewardsEditorProps {
  dealType: DealType;
  rewards: DealRewards;
  onChange: (rewards: DealRewards) => void;
}

const RARITY_OPTIONS = [
  { value: 0, label: 'Common' },
  { value: 1, label: 'Semi-Pro' },
  { value: 2, label: 'Pro' },
  { value: 3, label: 'Legendary' },
];

const DEFAULT_RARITY_WEIGHTS: Record<string, Record<number, number>> = {
  free_drop: { 0: 70, 1: 20, 2: 9, 3: 1 },
  player_of_day: { 0: 60, 1: 30, 2: 10, 3: 0 },
  legend_of_week: { 0: 0, 1: 0, 2: 0, 3: 100 },
};

interface RarityWeightsCardProps {
  dealType: string;
  rewards: DealRewards;
  onChange: (rewards: DealRewards) => void;
  description?: string;
}

const RarityWeightsCard: React.FC<RarityWeightsCardProps> = ({
  dealType,
  rewards,
  onChange,
  description,
}) => {
  const defaults = DEFAULT_RARITY_WEIGHTS[dealType] || { 0: 70, 1: 20, 2: 9, 3: 1 };
  const weights = rewards.rarityWeights || defaults;

  const handleChange = (rarity: number, value: number | null) => {
    onChange({
      ...rewards,
      rarityWeights: {
        ...weights,
        [rarity]: value || 0,
      },
    });
  };

  const total = RARITY_OPTIONS.reduce((sum, opt) => sum + (weights[opt.value] || 0), 0);

  return (
    <Card size="small" title="Card Rarity Weights (%)" style={{ marginTop: 16 }}>
      <Row gutter={16}>
        {RARITY_OPTIONS.map((opt) => (
          <Col span={6} key={opt.value}>
            <Form.Item label={`${opt.label} %`}>
              <InputNumber
                min={0}
                max={100}
                value={weights[opt.value] ?? 0}
                onChange={(v) => handleChange(opt.value, v)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
      <Text type={total === 100 ? 'success' : 'danger'}>
        Total: {total}% {total !== 100 && '(should equal 100%)'}
      </Text>
      {description && (
        <div style={{ marginTop: 4 }}>
          <Text type="secondary">{description}</Text>
        </div>
      )}
    </Card>
  );
};

export const RewardsEditor: React.FC<RewardsEditorProps> = ({ dealType, rewards, onChange }) => {
  // Pull the live max playerIndex so the Featured Player input accepts the
  // full active range — keeps the form in sync with roster expansions
  // without redeploying admin.
  const [maxPlayerIndex, setMaxPlayerIndex] = useState<number | null>(null);
  useEffect(() => {
    adminApi
      .getRosterSize()
      .then((res) => setMaxPlayerIndex(res.data.maxPlayerIndex))
      .catch(() => {
        /* keep null → fallback below */
      });
  }, []);
  const effectiveMaxIndex = maxPlayerIndex ?? DEFAULT_MAX_PLAYER_INDEX;

  if (dealType === 'free_drop') {
    const dropWeights = rewards.dropWeights || {
      coins: 63,
      matchTokens: 2,
      evoTokens: 20,
      cards: 5,
    };

    const handleDropWeightChange = (
      key: 'coins' | 'matchTokens' | 'evoTokens' | 'cards',
      value: number | null
    ) => {
      onChange({
        ...rewards,
        dropWeights: {
          ...dropWeights,
          [key]: value || 0,
        },
      });
    };

    const total =
      (dropWeights.coins || 0) +
      (dropWeights.matchTokens || 0) +
      (dropWeights.evoTokens || 0) +
      (dropWeights.cards || 0);

    const baseAmounts = rewards.baseAmounts || DEFAULT_BASE_AMOUNTS;
    const handleBaseAmountChange = (
      key: 'coins' | 'matchTokens' | 'evoTokens' | 'cards',
      value: number | null
    ) => {
      onChange({
        ...rewards,
        baseAmounts: {
          ...baseAmounts,
          [key]: value ?? 0,
        },
      });
    };

    const evoTokensByDivision = rewards.evoTokensByDivision || {};
    const handleEvoByDivisionChange = (tier: number, value: number | null) => {
      onChange({
        ...rewards,
        evoTokensByDivision: {
          ...evoTokensByDivision,
          [String(tier)]: value ?? 0,
        },
      });
    };

    return (
      <>
        <Card size="small" title="Drop Weights (Percentages)">
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Coins %">
                <InputNumber
                  min={0}
                  max={100}
                  value={dropWeights.coins}
                  onChange={(v) => handleDropWeightChange('coins', v)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Match Tokens %">
                <InputNumber
                  min={0}
                  max={100}
                  value={dropWeights.matchTokens}
                  onChange={(v) => handleDropWeightChange('matchTokens', v)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Evo Tokens %">
                <InputNumber
                  min={0}
                  max={100}
                  value={dropWeights.evoTokens}
                  onChange={(v) => handleDropWeightChange('evoTokens', v)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Cards %">
                <InputNumber
                  min={0}
                  max={100}
                  value={dropWeights.cards}
                  onChange={(v) => handleDropWeightChange('cards', v)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Text type={total === 100 ? 'success' : 'danger'}>
            Total: {total}% {total !== 100 && '(should equal 100%)'}
          </Text>
        </Card>
        <RarityWeightsCard
          dealType={dealType}
          rewards={rewards}
          onChange={onChange}
          description="Controls the card rarity when the random drop lands on 'Cards'."
        />
        <Card size="small" title="Base Drop Amounts (§2.7)" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label={`Coins (default ${DEFAULT_BASE_AMOUNTS.coins})`}>
                <InputNumber
                  min={0}
                  value={baseAmounts.coins}
                  onChange={(v) => handleBaseAmountChange('coins', v)}
                  style={{ width: '100%' }}
                  placeholder={`default: ${DEFAULT_BASE_AMOUNTS.coins}`}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={`Match Tokens (default ${DEFAULT_BASE_AMOUNTS.matchTokens})`}>
                <InputNumber
                  min={0}
                  value={baseAmounts.matchTokens}
                  onChange={(v) => handleBaseAmountChange('matchTokens', v)}
                  style={{ width: '100%' }}
                  placeholder={`default: ${DEFAULT_BASE_AMOUNTS.matchTokens}`}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={`Evo Tokens (default ${DEFAULT_BASE_AMOUNTS.evoTokens})`}>
                <InputNumber
                  min={0}
                  value={baseAmounts.evoTokens}
                  onChange={(v) => handleBaseAmountChange('evoTokens', v)}
                  style={{ width: '100%' }}
                  placeholder={`default: ${DEFAULT_BASE_AMOUNTS.evoTokens}`}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={`Cards (default ${DEFAULT_BASE_AMOUNTS.cards})`}>
                <InputNumber
                  min={0}
                  value={baseAmounts.cards}
                  onChange={(v) => handleBaseAmountChange('cards', v)}
                  style={{ width: '100%' }}
                  placeholder={`default: ${DEFAULT_BASE_AMOUNTS.cards}`}
                />
              </Form.Item>
            </Col>
          </Row>
          <Text type="secondary">
            Base amount granted before division scaling. Defaults shown in each label / placeholder
            mirror BASE_DROP_AMOUNTS in daily-drop-reward.service.ts; clearing a field falls back to
            that constant.
          </Text>
        </Card>
        <Card size="small" title="Evo Tokens by Division (§2.7)" style={{ marginTop: 16 }}>
          <Text type="secondary">
            Override the per-tier evo-token amount granted to players in each division. Defaults
            shown in the placeholder mirror EVO_TOKENS_BY_DIVISION in daily-drop-reward.service.ts;
            clearing a row falls back to that constant.
          </Text>
          <Table
            size="small"
            dataSource={DIVISION_TIERS}
            rowKey="tier"
            pagination={false}
            style={{ marginTop: 12 }}
            columns={[
              { title: 'Tier', dataIndex: 'tier', width: 80 },
              { title: 'Division', dataIndex: 'name' },
              {
                title: 'Default',
                dataIndex: 'defaultEvo',
                width: 90,
                render: (v: number) => <Text type="secondary">{v}</Text>,
              },
              {
                title: 'Evo Tokens',
                width: 160,
                render: (_: unknown, record: { tier: number; defaultEvo: number }) => (
                  <InputNumber
                    min={0}
                    value={evoTokensByDivision[String(record.tier)]}
                    onChange={(v) => handleEvoByDivisionChange(record.tier, v)}
                    style={{ width: '100%' }}
                    placeholder={`default: ${record.defaultEvo}`}
                  />
                ),
              },
            ]}
          />
        </Card>
      </>
    );
  }

  // Free Coin Drop - Fixed coins reward
  if (dealType === 'free_coin_drop') {
    return (
      <Card size="small" title="Coin Reward">
        <Form.Item label="Coins Amount">
          <InputNumber
            min={0}
            value={rewards.coins}
            onChange={(v) => onChange({ ...rewards, coins: v || undefined })}
            style={{ width: '100%' }}
            placeholder="250"
          />
        </Form.Item>
        <Text type="secondary">Fixed coin amount granted when claimed.</Text>
      </Card>
    );
  }

  // Free Card Drop - Fixed card reward
  if (dealType === 'free_card_drop') {
    return (
      <Card size="small" title="Card Reward">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Card Rarity">
              <Select
                value={rewards.cardRarity}
                onChange={(v) => onChange({ ...rewards, cardRarity: v })}
                style={{ width: '100%' }}
                placeholder="Select rarity"
                allowClear
              >
                {RARITY_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Card Count">
              <InputNumber
                min={0}
                max={10}
                value={rewards.cardCount}
                onChange={(v) => onChange({ ...rewards, cardCount: v || undefined })}
                style={{ width: '100%' }}
                placeholder="1"
              />
            </Form.Item>
          </Col>
        </Row>
        <Text type="secondary">Fixed card reward granted when claimed.</Text>
      </Card>
    );
  }

  // Player of Day / Legend of Week - Card rewards + Currency rewards + Rarity weights
  return (
    <>
      <Card size="small" title="Card Rewards" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Card Rarity">
              <Select
                value={rewards.cardRarity}
                onChange={(v) => onChange({ ...rewards, cardRarity: v })}
                style={{ width: '100%' }}
                placeholder="Select rarity"
                allowClear
              >
                {RARITY_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Card Count">
              <InputNumber
                min={0}
                max={10}
                value={rewards.cardCount}
                onChange={(v) => onChange({ ...rewards, cardCount: v || undefined })}
                style={{ width: '100%' }}
                placeholder="0 = No cards"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Featured Player Index">
              <InputNumber
                min={0}
                max={effectiveMaxIndex}
                value={rewards.featuredPlayerIndex}
                onChange={(v) => onChange({ ...rewards, featuredPlayerIndex: v ?? undefined })}
                style={{ width: '100%' }}
                placeholder="Auto (empty)"
              />
            </Form.Item>
          </Col>
        </Row>
        <Text type="secondary">
          Set a specific player (0-{effectiveMaxIndex}) or leave empty for auto-rotation based on
          date.
        </Text>
      </Card>
      <RarityWeightsCard
        dealType={dealType}
        rewards={rewards}
        onChange={onChange}
        description={
          dealType === 'player_of_day'
            ? 'Probability of each rarity when granting the featured player card.'
            : 'Probability of each rarity for legend of the week card.'
        }
      />
      <Card size="small" title="Currency Rewards (Optional)" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Coins">
              <InputNumber
                min={0}
                value={rewards.coins}
                onChange={(v) => onChange({ ...rewards, coins: v || undefined })}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Match Tokens">
              <InputNumber
                min={0}
                value={rewards.matchTokens}
                onChange={(v) => onChange({ ...rewards, matchTokens: v || undefined })}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Evo Tokens">
              <InputNumber
                min={0}
                value={rewards.evoTokens}
                onChange={(v) => onChange({ ...rewards, evoTokens: v || undefined })}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Gems">
              <InputNumber
                min={0}
                value={rewards.premiumCurrency}
                onChange={(v) => onChange({ ...rewards, premiumCurrency: v || undefined })}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
        </Row>
        <Text type="secondary">
          Currency rewards are granted in addition to card rewards when the deal is purchased.
        </Text>
      </Card>
    </>
  );
};

export default RewardsEditor;
